import React from 'react';
import { DestinationWarning as DestinationWarningType } from '@/features/planner/api/planner';
import { Box, Typography } from '@mui/material';
import { AlertTriangle, Info } from 'lucide-react';

interface DestinationWarningProps {
    warning: DestinationWarningType[];
}

export const DestinationWarning: React.FC<DestinationWarningProps> = ({ warning }) => {
    // If no warnings, render nothing
    if (!warning || warning.length === 0) return null;

    // Determine highest severity
    const isHigh = warning.some(w => w.severity === 'high');
    const isMedium = !isHigh && warning.some(w => w.severity === 'medium');

    // Default to low/info style
    let styles = {
        borderColor: '#0288d1',
        color: '#0288d1',
        backgroundColor: '#e3f2fd',
        icon: <Info size={32} />
    };

    if (isHigh) {
        styles = {
            borderColor: '#B91C1C', // Red
            color: '#B91C1C',
            backgroundColor: '#FFF5F5', // Very light red/cream
            icon: <AlertTriangle size={36} />
        };
    } else if (isMedium) {
        styles = {
            borderColor: '#C2410C', // Orange-ish
            color: '#C2410C',
            backgroundColor: '#FFF7ED', // Orange 50
            icon: <AlertTriangle size={36} />
        };
    }

    return (
        <Box
            sx={{
                border: `1.5px solid ${styles.borderColor}`,
                backgroundColor: styles.backgroundColor,
                borderRadius: '16px',
                padding: '20px 24px',
                display: 'flex',
                alignItems: 'flex-start', // Align start for multiple lines
                gap: 3,
                marginBottom: 3,
            }}
        >
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                {warning.map((w, index) => (
                    <Typography
                        key={index}
                        variant="body1"
                        sx={{
                            color: styles.color,
                            fontWeight: 500,
                            fontSize: '16px',
                            lineHeight: 1.4,
                        }}
                    >
                        {w.message}
                    </Typography>
                ))}
            </Box>
        </Box>
    );
};
